// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import api from '../api';
import { trackEvent, identifyUser } from '../utils/analytics';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user subscription data and add it to the user object
  const fetchAndUpdateSubscription = async (userData) => {
    try {
      const subscriptionRes = await api.payments.getSubscription();
      const updatedUser = {
        ...userData,
        subscription: subscriptionRes.data
      };
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Error fetching subscription:', err);
      // Still update the user, just without subscription
      setUser(userData);
      return userData;
    }
  };

  // Update subscription data directly without fetching from API
  const updateSubscription = (newSubscriptionData) => {
    if (!user) return;
    
    const updatedUser = {
      ...user,
      subscription: {
        ...user.subscription,
        ...newSubscriptionData
      }
    };
    
    // Update state
    setUser(updatedUser);
    
    // Optionally update localStorage if you're caching user data
    if (localStorage.getItem('user')) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    
    return updatedUser;
  };

  // Initialize auth state on page load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set the token in API client
          api.setAuthToken(token);
          
          // Get current user
          const response = await api.auth.me();
          
          // Fetch subscription and update user
          await fetchAndUpdateSubscription(response.data);
          
          // Track user identity in analytics
          if (response.data?.id) {
            identifyUser(response.data.id, {
              email: response.data.email,
              user_type: response.data.user_type || 'standard'
            });
          }
        } catch (err) {
          console.error('Auth initialization error:', err);
          // Clear invalid token
          localStorage.removeItem('token');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // Call the login API
      const response = await api.auth.login(email, password);
      
      // Check for custom error format
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      
      // Get access token from response
      const { access_token } = response.data;
      
      if (!access_token) {
        throw new Error("No access token received");
      }
      
      // Save token to localStorage
      localStorage.setItem('token', access_token);
      
      // Set the token in API client
      api.setAuthToken(access_token);
      
      // Get user data
      const userResponse = await api.auth.me();
      
      // Fetch subscription and update user
      const userData = await fetchAndUpdateSubscription(userResponse.data);
      
      // Track login success event
      trackEvent('login', 'authentication', 'success');
      
      // Track user identity in analytics
      if (userData?.id) {
        identifyUser(userData.id, {
          email: userData.email,
          user_type: userData.user_type || 'standard'
        });
      }
      
      setError(null);
      return userData;
    } catch (err) {
      console.error('Login error:', err);
      
      // Format error message
      let errorMessage = 'Login failed';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.detail) {
        // Standard FastAPI error format
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : 'Invalid credentials';
      }
      
      // Track login failure event
      trackEvent('login_error', 'authentication', errorMessage);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await api.auth.register(userData);
      
      // Track successful registration
      trackEvent('registration', 'authentication', 'success');
      
      return response.data;
    } catch (err) {
      console.error('Registration error:', err);
      
      // Format error message
      let errorMessage = 'Registration failed';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        errorMessage = typeof detail === 'string' 
          ? detail 
          : (detail.msg || 'Registration failed. Please try again.');
      }
      
      // Track registration failure
      trackEvent('registration_error', 'authentication', errorMessage);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // Track logout event before clearing user data
    if (user) {
      trackEvent('logout', 'authentication', 'success');
    }
    
    localStorage.removeItem('token');
    api.clearAuthToken();
    setUser(null);
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      const response = await api.auth.refreshToken();
      const { access_token } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', access_token);
      
      // Set the token in API client
      api.setAuthToken(access_token);
      
      return access_token;
    } catch (err) {
      console.error('Token refresh error:', err);
      // If refresh fails, log out
      logout();
      throw err;
    }
  };

  // Update user subscription status from elsewhere in the app
  const refreshSubscription = async () => {
    if (user) {
      try {
        await fetchAndUpdateSubscription(user);
        return true;
      } catch (err) {
        console.error('Error refreshing subscription:', err);
        return false;
      }
    }
    return false;
  };

  // Check if token is expired
  const isTokenExpired = () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    
    try {
      // Parse JWT token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiry;
    } catch (e) {
      return true;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshToken,
    isTokenExpired,
    refreshSubscription,
    updateSubscription,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};